import React, { useState, useEffect, useMemo } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import Modal from './Modal';
import BlockTitle from './BlockTitle';
import Button from './Button';
import { useToast } from '../context/ToastContext';
import {
  DfgSection,
  DfgField,
  DfgSelect,
  RowCard,
  AddRowButton,
  DfgErrors,
  DfgPreview,
} from './DfgFormParts';
import {
  LEGAL_STATUS_OPTIONS,
  toIban,
  prefillZawmrpForm,
  buildZawmrpRecord,
  validateZawmrp,
  buildDfgFilename,
  serializeRecords,
  downloadDfgZip,
  downloadDfgJson,
} from '../lib/dfgGenerator';

const emptyPermit = () => ({ permitNumber: '', authorityName: '' });
const emptyParcel = () => ({ landRegisterNumber: '', area: '', legalStatus: 'Własność' });

const GenerateZawmrpModal = ({ isOpen, onClose, investmentId, investment }) => {
  const { showSuccess, showError } = useToast();

  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(prefillZawmrpForm(investment));
    setErrors([]);
    setShowPreview(false);
  }, [isOpen, investment]);

  const record = useMemo(() => (form ? buildZawmrpRecord(form) : null), [form]);

  const percentageSum = useMemo(() => {
    if (!form) return 0;
    return Number(
      form.schedules.reduce((acc, s) => acc + (Number(s.percentageCost) || 0), 0).toFixed(2)
    );
  }, [form]);

  if (!isOpen || !form) return null;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setRow = (listKey, index, key, value) =>
    setForm((prev) => ({
      ...prev,
      [listKey]: prev[listKey].map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    }));

  const addRow = (listKey, factory) =>
    setForm((prev) => ({ ...prev, [listKey]: [...prev[listKey], factory()] }));

  const removeRow = (listKey, index) =>
    setForm((prev) => ({ ...prev, [listKey]: prev[listKey].filter((_, i) => i !== index) }));

  const persist = async () => {
    await setDoc(
      doc(firestore, `investments/${investmentId}`),
      {
        dfg: {
          mrpAccountNumber: toIban(form.mrpAccountNumber),
          buildingPermits: form.buildingPermits,
          schedules: form.schedules.map((s) => ({
            stage: Number(s.stage) || 0,
            percentageCost: Number(s.percentageCost) || 0,
            deadline: s.deadline || '',
          })),
          developerParcels: form.developerParcels.map((p) => ({
            ...p,
            area: Number(p.area) || 0,
          })),
        },
      },
      { merge: true }
    );
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      await persist();
      showSuccess('Dane DFG inwestycji zapisane');
    } catch (e) {
      console.error(e);
      showError('Nie udało się zapisać danych DFG');
    } finally {
      setBusy(false);
    }
  };

  const handleGenerate = async (format) => {
    const found = validateZawmrp(form);
    setErrors(found);
    if (found.length > 0) return;

    setBusy(true);
    try {
      await persist();
      const today = new Date().toISOString().split('T')[0];
      const filename = buildDfgFilename('ZAWMRP', investment?.name || 'Inwestycja', today);

      if (format === 'json') {
        const result = downloadDfgJson([record], filename);
        showSuccess(`Pobrano ${result.filename} (${(result.bytes / 1024).toFixed(1)} kB)`);
      } else {
        const result = await downloadDfgZip([record], filename);
        showSuccess(`Wygenerowano ${result.filename} (${(result.zipBytes / 1024).toFixed(1)} kB)`);
      }
    } catch (e) {
      console.error(e);
      showError(e.message || 'Nie udało się wygenerować pliku');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} wide>
      <BlockTitle>ZAWMRP - rejestracja rachunku powierniczego</BlockTitle>
      <p className="text-sm text-gray-500 mt-1">
        {investment?.name || 'Inwestycja'} - dane wstępnie uzupełnione z systemu, wszystko można
        poprawić przed wygenerowaniem pliku.
      </p>

      <DfgSection title="Rachunek">
        <DfgField
          id="dfg-mrp"
          label="Główny rachunek MRP (IBAN)"
          value={form.mrpAccountNumber}
          onChange={(e) => setField('mrpAccountNumber', e.target.value)}
          placeholder="PL00000000000000000000000000"
        />
        <p className="text-xs text-gray-500 mt-1">
          Ten sam numer trafi do każdego zgłoszenia ZAUMDE lokali z tej inwestycji.
        </p>
      </DfgSection>

      <DfgSection
        title="Pozwolenia na budowę"
        action={<AddRowButton onClick={() => addRow('buildingPermits', emptyPermit)}>Dodaj pozwolenie</AddRowButton>}
      >
        {form.buildingPermits.map((permit, i) => (
          <RowCard
            key={i}
            removable={form.buildingPermits.length > 1}
            onRemove={() => removeRow('buildingPermits', i)}
          >
            <div className="grid grid-cols-2 max-768:grid-cols-1 gap-3 pr-6">
              <DfgField
                id={`permit-number-${i}`}
                label="Numer pozwolenia"
                value={permit.permitNumber}
                onChange={(e) => setRow('buildingPermits', i, 'permitNumber', e.target.value)}
                placeholder="3011/1987"
              />
              <DfgField
                id={`permit-authority-${i}`}
                label="Organ wydający"
                value={permit.authorityName}
                onChange={(e) => setRow('buildingPermits', i, 'authorityName', e.target.value)}
                placeholder="Prezydent Miasta Poznania"
              />
            </div>
          </RowCard>
        ))}
      </DfgSection>

      <DfgSection
        title="Harmonogram przedsięwzięcia"
        hint={`Suma procentów: ${percentageSum}% (musi wynosić 100%)`}
        action={
          <AddRowButton
            onClick={() =>
              addRow('schedules', () => ({
                stage: form.schedules.length + 1,
                percentageCost: '',
                deadline: '',
              }))
            }
          >
            Dodaj etap
          </AddRowButton>
        }
      >
        {form.schedules.map((stage, i) => (
          <RowCard
            key={i}
            removable={form.schedules.length > 1}
            onRemove={() => removeRow('schedules', i)}
          >
            <div className="grid grid-cols-3 max-768:grid-cols-1 gap-3 pr-6">
              <DfgField
                id={`stage-number-${i}`}
                label="Etap"
                type="number"
                min="1"
                value={stage.stage}
                onChange={(e) => setRow('schedules', i, 'stage', e.target.value)}
              />
              <DfgField
                id={`stage-percent-${i}`}
                label="Procent kosztów"
                type="number"
                step="0.01"
                min="0"
                value={stage.percentageCost}
                onChange={(e) => setRow('schedules', i, 'percentageCost', e.target.value)}
              />
              <DfgField
                id={`stage-deadline-${i}`}
                label="Termin"
                type="date"
                value={stage.deadline}
                onChange={(e) => setRow('schedules', i, 'deadline', e.target.value)}
              />
            </div>
          </RowCard>
        ))}
      </DfgSection>

      <DfgSection
        title="Działki deweloperskie"
        action={<AddRowButton onClick={() => addRow('developerParcels', emptyParcel)}>Dodaj działkę</AddRowButton>}
      >
        {form.developerParcels.map((parcel, i) => (
          <RowCard
            key={i}
            removable={form.developerParcels.length > 1}
            onRemove={() => removeRow('developerParcels', i)}
          >
            <div className="grid grid-cols-3 max-768:grid-cols-1 gap-3 pr-6">
              <DfgField
                id={`parcel-kw-${i}`}
                label="Numer księgi wieczystej"
                value={parcel.landRegisterNumber}
                onChange={(e) => setRow('developerParcels', i, 'landRegisterNumber', e.target.value)}
                placeholder="PO1P/00012345/6"
              />
              <DfgField
                id={`parcel-area-${i}`}
                label="Powierzchnia (m2)"
                type="number"
                step="0.01"
                min="0"
                value={parcel.area}
                onChange={(e) => setRow('developerParcels', i, 'area', e.target.value)}
              />
              <DfgSelect
                id={`parcel-status-${i}`}
                label="Stan prawny"
                value={parcel.legalStatus}
                onChange={(e) => setRow('developerParcels', i, 'legalStatus', e.target.value)}
                options={LEGAL_STATUS_OPTIONS}
              />
            </div>
          </RowCard>
        ))}
      </DfgSection>

      <DfgErrors errors={errors} />

      <DfgPreview
        open={showPreview}
        onToggle={() => setShowPreview((v) => !v)}
        json={serializeRecords([record])}
      />

      <div className="flex justify-end gap-3 mt-6">
        <Button variant="bordered" onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button variant="bordered" onClick={handleSave} disabled={busy}>
          Zapisz dane
        </Button>
        <Button
          variant="bordered"
          title="Sam plik JSON, bez pakowania - do wglądu i archiwum"
          onClick={() => handleGenerate('json')}
          disabled={busy}
        >
          Pobierz JSON
        </Button>
        <Button
          variant="default"
          title="Plik do wgrania na portal DFG"
          onClick={() => handleGenerate('zip')}
          disabled={busy}
        >
          {busy ? 'Pracuję...' : 'Generuj plik ZIP'}
        </Button>
      </div>
    </Modal>
  );
};

export default GenerateZawmrpModal;
